import { useState } from "react";
import { getPresentationParamsForOrigin } from "../../../getPresentationParamsForOrigin.ts";
import { useCommand } from "../../../hooks/useCommand.ts";
import { CompetitionCommand } from "../../../commandTypes.ts";
import { useCompetitionList } from "../../../hooks/useCompetitionList.ts";
import { useCompetition } from "../../../hooks/useCompetition.ts";
import {
  ActionIcon,
  Box,
  Card,
  CopyButton,
  Grid,
  Group,
  List,
  Loader,
  Select,
  Stack,
  TextInput,
  Title,
  Flex,
  AspectRatio,
} from "@mantine/core";
import { Page } from "../../../Components/Page.tsx";
import { IconCheck, IconCopy } from "@tabler/icons-react";

export const StreamElementLinksOverview = () => {
  const [competitionId, setCompetitionId] = useState<string>(
    getPresentationParamsForOrigin().competitionId,
  );
  useCommand<CompetitionCommand>("competition", (c) => {
    setCompetitionId(c.competitionId);
  });

  const competitionListData = useCompetitionList();
  const competitionData = useCompetition(competitionId);

  if (!competitionListData.isSuccess || !competitionData.isSuccess) {
    return <Loader />;
  }

  // true if page is served via https
  const showCopyButton = window.location.protocol === "https:";

  const groups = competitionData.data.groups;

  const streamLinks: { label: string; url: string }[] = [
    {
      label: "Spielstand",
      url: `${window.location.origin}/streamelements/score`,
    },
    {
      label: "Torschütze",
      url: `${window.location.origin}/streamelements/goal-scorer`,
    },
    {
      label: "9m-Schießen",
      url: `${window.location.origin}/streamelements/shootout`,
    },
    {
      label: "Nächstes Spiel",
      url: `${window.location.origin}/streamelements/next-match`,
    },
    ...groups.map((group) => ({
      label: `Tabelle: ${group.name}`,
      url: `${window.location.origin}/streamelements/${competitionId}/${group.id}/leaderboard`,
    })),
    {
      label: "Topscorer",
      url: `${window.location.origin}/streamelements/topscorer`,
    },
  ];

  const displayLinks: { label: string; url: string }[] = [
    {
      label: "Regie-Status/Timer",
      url: `${window.location.origin}/streamelements/countdown`,
    },
    {
      label: "Turnier-Übersicht",
      url: `${window.location.origin}/streamelements/competition`,
    },
    {
      label: "Kader",
      url: `${window.location.origin}/streamelements/squad-fullscreen`,
    },
    {
      label: "Topscorer",
      url: `${window.location.origin}/streamelements/topscorer-fullscreen`,
    },
    ...groups.map((group) => ({
      label: `Tabelle: ${group.name}`,
      url: `${window.location.origin}/streamelements/${competitionId}/${group.id}/leaderboard-fullscreen`,
    })),
  ];

  return (
    <Page title="Grafiken">
      <Select
        my="md"
        label="Turnier"
        value={competitionId}
        onChange={(v) => {
          if (!v) {
            return;
          }
          setCompetitionId(v);
        }}
        data={competitionListData.data.competitions.map((c) => ({
          value: c.id,
          label: c.name,
        }))}
      />
      {competitionId && (
        <Stack gap="xl">
          <Box>
            <Title order={2} mb="lg">
              Stream-Overlays
            </Title>
            <Grid gutter="md">
              {streamLinks.map((link) => (
                <Grid.Col
                  span={{
                    xs: 6,
                    md: 4,
                  }}
                  key={link.label}
                >
                  <Card shadow="lg" padding="md" radius="md" withBorder>
                    <Card.Section>
                      <AspectRatio ratio={16 / 9}>
                        <iframe
                          src={`${link.url}?showBackground`}
                          width="100%"
                          height="100%"
                          scrolling="no"
                          style={{
                            zoom: 0.2,
                          }}
                        />
                      </AspectRatio>
                    </Card.Section>
                    <Group justify="space-between" mt="md" mb="xs">
                      <Title order={3}>{link.label}</Title>
                    </Group>
                    <Flex align="center" gap="md">
                      <TextInput value={link.url} style={{ flexGrow: 1 }} />
                      {showCopyButton && (
                        <CopyButton value={link.url}>
                          {({ copied, copy }) => (
                            <ActionIcon
                              color={copied ? "teal" : "gray"}
                              variant="subtle"
                              onClick={copy}
                            >
                              {copied ? (
                                <IconCheck size={16} />
                              ) : (
                                <IconCopy size={16} />
                              )}
                            </ActionIcon>
                          )}
                        </CopyButton>
                      )}
                    </Flex>
                  </Card>
                </Grid.Col>
              ))}
            </Grid>
          </Box>
          <Box>
            <Title order={2} mb="lg">
              Displays
            </Title>
            <List>
              {displayLinks.map((link) => (
                <List.Item key={link.label}>
                  <strong>{link.label}</strong>
                  <div>
                    <a href={link.url} target="_blank" rel="noreferrer">
                      {link.url}
                    </a>
                  </div>
                </List.Item>
              ))}
            </List>
          </Box>
        </Stack>
      )}
    </Page>
  );
};
